import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import {
  AccountGroup,
  AccountGroupApiService,
  Constraint,
  LookupRequestInterface,
  ProjectionFilter,
} from '@vendasta/account-group';
import { BehaviorSubject, Subscription, combineLatest } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, shareReplay, startWith, switchMap } from 'rxjs/operators';

const PAGE_SIZE = 20;

export interface DialogData {
  partnerId: string;
  marketId: string;
  projectionFilterOverride?: ProjectionFilter;
}
@Component({
  templateUrl: './business-selector-dialog.component.html',
  styleUrls: ['./business-selector-dialog.component.scss'],
})
export class BusinessSelectorDialogComponent implements OnInit, OnDestroy {
  private readonly loadMore$$ = new BehaviorSubject<boolean>(false);
  private readonly loadMoreTrigger$ = this.loadMore$$.asObservable().pipe(map(() => null));
  private readonly business$$ = new BehaviorSubject<AccountGroup[]>([]);
  protected readonly business$ = this.business$$.asObservable();
  private readonly nextCursor$$ = new BehaviorSubject<string>('');
  private readonly hasMore$$ = new BehaviorSubject<boolean>(false);
  protected readonly hasMore$ = this.hasMore$$.asObservable();
  private readonly loading$$ = new BehaviorSubject<boolean>(true);
  protected readonly loading$ = this.loading$$.asObservable();
  private readonly subscriptions: Subscription[] = [];

  protected readonly searchFormField = new UntypedFormControl('');
  private previousSearchTerm = '';
  protected loadingFirstPage = true;

  constructor(
    private readonly dialogRef: MatDialogRef<BusinessSelectorDialogComponent>,
    private readonly router: Router,
    private readonly accountGroupService: AccountGroupApiService,
    @Inject(MAT_DIALOG_DATA) private readonly data: DialogData,
  ) {}

  ngOnInit(): void {
    this.initBusinesses();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  loadMoreAccounts(inViewportEvent: any): void {
    if (inViewportEvent.visible) {
      this.loadMore$$.next(true);
    }
  }

  // initBusinesses sets up the subscription to load in account groups, responding to changes in search, scroll, and partner ID
  private initBusinesses(): void {
    this.subscriptions.push(
      combineLatest([this.searchFormField.valueChanges.pipe(startWith('')), this.loadMoreTrigger$])
        .pipe(
          debounceTime(500),
          distinctUntilChanged(),
          shareReplay(1),
          switchMap(([searchTerm]) => {
            this.loading$$.next(true);
            let cursor = this.nextCursor$$.value;
            if (this.previousSearchTerm !== searchTerm) {
              // reset cursor and current results when new search terms entered
              cursor = '';
              this.loadingFirstPage = true;
              this.business$$.next([]);
            }
            this.previousSearchTerm = searchTerm;
            const projectionFilter =
              this.data.projectionFilterOverride ||
              new ProjectionFilter({
                napData: true,
                accountGroupExternalIdentifiers: true,
                constraints: this.data.partnerId === 'VMF',
              });
            const lookupRequest: LookupRequestInterface = {
              projectionFilter: projectionFilter,
              filters: {
                partnerId: this.data.partnerId,
                includeDeleted: false,
              },
              pageSize: PAGE_SIZE,
              searchTerm: searchTerm ? searchTerm : this.searchFormField.value,
              cursor: cursor,
              sortOptions: {
                direction: 1,
                field: 1,
              },
            };
            if (this.data.marketId) {
              lookupRequest.filters.marketIds = [this.data.marketId];
            }
            return this.accountGroupService.lookup(lookupRequest);
          }),
        )
        .subscribe({
          next: (pagedAccountGroupResponse) => {
            // append the results to the current list
            const businesses = pagedAccountGroupResponse.accountGroups ?? [];
            this.business$$.next(this.business$$.value.concat(businesses));
            this.hasMore$$.next(pagedAccountGroupResponse.hasMore);
            this.nextCursor$$.next(pagedAccountGroupResponse.nextCursor);
            this.loading$$.next(false);
            this.loadingFirstPage = false;
          },
          error: () => {
            this.loading$$.next(false);
            this.loadingFirstPage = false;
          },
        }),
    );
  }

  selectBusiness(accountGroup: AccountGroup): void {
    this.dialogRef.close(accountGroup);
  }

  createBusiness(): void {
    this.dialogRef.close();
    this.router.navigateByUrl(`/business/search`);
  }

  getPartnerIdfromConstraints(constraints: Constraint[]): string {
    return constraints.find((element) => element?.foreignKey?.kind === 'PartnerAccountGroup')?.foreignKey?.key;
  }

  close(): void {
    this.dialogRef.close();
  }
}
